<template>
  <section id="how-it-works" ref="how-it-works" :class="$style.section">
    <div class="layout-container">
      <h2 :class="$style.title">{{ t('myProperties.landing.howItWorks.title') }}</h2>

      <div :class="$style.wrapper">
        <div :class="$style.imageSection">
          <div :class="$style.imageContainer">
            <img :src="backgroundImage" alt="" :class="$style.bgImage" loading="lazy" />
            <img :src="phoneImage" alt="" :class="$style.phoneImage" loading="lazy" />
          </div>
        </div>

        <div :class="$style.stepsSection">
          <div v-for="(step, index) in steps" :key="index" :class="$style.step">
            <div :class="$style.stepHeader">
              <span
                :class="[$style.stepNumber, isHg ? $style.stepNumberHg : $style.stepNumberIs24]"
              >
                {{ index + 1 }}
              </span>
              <div :class="$style.stepContent">
                <h3 :class="$style.stepTitle">{{ step.title }}</h3>
                <p :class="$style.stepDescription" v-html="step.description"></p>
              </div>
            </div>
          </div>

          <div :class="$style.stepHeader">
            <span :class="$style.stepNumber" aria-hidden="true"></span>
            <HgButton
              size="large"
              :theme="isHg ? 'secondary' : 'primary'"
              :class="$style.cta"
              @click="goToOverview"
            >
              {{ t('myProperties.landing.hero.visualCta') }}
            </HgButton>
          </div>

          <p :class="$style.note" v-html="t('myProperties.landing.howItWorks.note')"></p>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { isPlatformHg } from '@/helpers/Platform/Platform';
import { getPropertyLandingImageUrl } from '@/helpers/Url/Url';
import { OVERVIEW_PAGE_PATH } from '@/router/routes/constants';
import { trackingClickEvent } from '@/tracking/tracking';
import { TRACKING_ACTION, TRACKING_LABEL } from '@/tracking/types';
import { HgButton, HgSupportedLanguage } from '@smg-real-estate/domus';
import { computed, onMounted, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

const isHg = isPlatformHg();
const { locale, t } = useI18n();
const router = useRouter();
const platformSuffix = isHg ? 'hg' : 'is24';

const backgroundImage = getPropertyLandingImageUrl(`how-it-works/bg-${platformSuffix}.png`);

const phoneImagesHg: Record<HgSupportedLanguage, string> = {
  [HgSupportedLanguage.DE]: getPropertyLandingImageUrl(`how-it-works/phone-hg-de-new_v2.png`),
  [HgSupportedLanguage.EN]: getPropertyLandingImageUrl(`how-it-works/phone-hg-en-new_v2.png`),
  [HgSupportedLanguage.FR]: getPropertyLandingImageUrl(`how-it-works/phone-hg-fr-new_v2.png`),
  [HgSupportedLanguage.IT]: getPropertyLandingImageUrl(`how-it-works/phone-hg-it-new_v2.png`),
};

const phoneImagesIs24: Record<HgSupportedLanguage, string> = {
  [HgSupportedLanguage.DE]: getPropertyLandingImageUrl(`how-it-works/phone-is24-de-new_v2.png`),
  [HgSupportedLanguage.EN]: getPropertyLandingImageUrl(`how-it-works/phone-is24-en-new_v2.png`),
  [HgSupportedLanguage.FR]: getPropertyLandingImageUrl(`how-it-works/phone-is24-fr-new_v2.png`),
  [HgSupportedLanguage.IT]: getPropertyLandingImageUrl(`how-it-works/phone-is24-it-new_v2.png`),
};

const phoneImage = computed(() => {
  const imagesByLocale = isHg ? phoneImagesHg : phoneImagesIs24;
  const currentLocale = locale.value as HgSupportedLanguage;

  return imagesByLocale[currentLocale] ?? imagesByLocale[HgSupportedLanguage.EN];
});

const componentRef = useTemplateRef('how-it-works');
const route = useRoute();
onMounted(() => {
  if (route.hash === '#how-it-works') {
    componentRef.value?.scrollIntoView({ behavior: 'smooth' });
  }
});

const steps = computed(() => [
  {
    title: t('myProperties.landing.howItWorks.step1.title'),
    description: t('myProperties.landing.howItWorks.step1.description'),
  },
  {
    title: t('myProperties.landing.howItWorks.step2.title'),
    description: t('myProperties.landing.howItWorks.step2.description'),
  },
  {
    title: t('myProperties.landing.howItWorks.step3.title'),
    description: t('myProperties.landing.howItWorks.step3.description'),
  },
]);

const goToOverview = () => {
  trackingClickEvent(TRACKING_ACTION.GO_TO_MY_REAL_ESTATE, TRACKING_LABEL.HOW_IT_WORKS);
  router.push(`${OVERVIEW_PAGE_PATH[locale.value]}`);
};
</script>

<style lang="scss" module>
@use '@/assets/variables' as *;

.section {
  padding: $space-100 0;
  background-color: $color-white;
}

.title {
  @include heading-1;
  margin: 0 0 $space-300 0;
  color: $color-black;
  text-align: center;

  @include grid-media-query(max-sm) {
    font-size: 24px;
  }
}

.wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: $space-300;

  @include grid-media-query(sm) {
    flex-direction: row;
    align-items: stretch;
    gap: $space-400;
  }
}

.imageSection {
  width: 100%;

  @include grid-media-query(sm) {
    flex: 0 0 40%;
    max-width: 40%;
  }
}

.imageContainer {
  --phone-cutoff: 25%;

  position: relative;
  width: 100%;
  overflow: hidden;
  border-radius: $border-radius-l;
  aspect-ratio: 5 / 7;
  min-height: 300px;
  max-height: 500px;

  @include grid-media-query(sm) {
    aspect-ratio: unset;
    height: 100%;
    min-height: 350px;
    max-height: 550px;
  }
}

.bgImage {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.phoneImage {
  position: absolute;
  bottom: calc(-1 * var(--phone-cutoff));
  left: $space-50;
  right: $space-50;
  width: calc(100% - 2 * $space-50);
  max-height: calc(100% - $space-100 + var(--phone-cutoff));
  object-fit: contain;
  object-position: bottom;
}

.stepsSection {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: $space-300;
}

.step {
  display: flex;
  flex-direction: column;
  gap: $space-50;
}

.stepHeader {
  display: flex;
  align-items: flex-start;
  gap: $space-100;
}

.stepNumber {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  font-weight: 800;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0;
  text-align: center;
}

.stepNumberHg {
  background-color: #e6efff;
  color: #1e50ad;
}

.stepNumberIs24 {
  background-color: transparent;
  border: 3px solid #333;
  color: #333;
}

.stepTitle {
  @include heading-3;
  margin: 0;
  color: $color-black;
}

.stepContent {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: $space-50;
}

.stepDescription {
  @include body-2;
  margin: 0;
  color: $color-black;

  b {
    font-weight: 600;
  }
}

.cta {
  width: fit-content;
  color: $color-blue-60;
}

.note {
  @include body-2;
  margin: 0;
  color: $color-black;
}
</style>
